import { useEffect, useRef, useState } from "react";
import { Ban, Check, ImagePlus, Loader2, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { useLook } from "@/context/LookContext";
import { api, errMsg } from "@/lib/api";
import { imageOk, useAuthedSrc } from "@/lib/authedImage";
import { galleryThumb } from "@/lib/themes";
import AiWallpaper from "@/components/look/AiWallpaper";

const BAD = "Upload a JPG, PNG, WEBP or HEIC under 15 MB";

function Tile({ active, onClick, testid, title, children, footer }) {
  return (
    <div className="group overflow-hidden rounded-xl border bg-q-surface transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5"
      style={{ borderColor: active ? "rgb(var(--q-accent))" : "rgb(var(--q-border))", boxShadow: active ? "0 0 0 1px rgb(var(--q-accent))" : "none" }}
      data-testid={testid} data-active={active ? "1" : "0"}>
      <button className="relative block aspect-[16/10] w-full overflow-hidden bg-q-raised" onClick={onClick} aria-pressed={active} data-testid={`${testid}-pick`} title={title}>
        {children}
        {active && <span className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-q-accent text-q-bg"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>}
      </button>
      <div className="px-3 py-2">
        <p className="truncate text-[12.5px] font-semibold text-q-text">{title}</p>
        {footer}
      </div>
    </div>
  );
}

function MineTile({ w, active, onUse, onDelete }) {
  const src = useAuthedSrc(`/me/wallpapers/${w.id}/file`);
  return (
    <Tile active={active} onClick={onUse} testid={`my-wallpaper-${w.id}`} title={w.title}
      footer={
        <div className="mt-1.5 flex items-center justify-between gap-1">
          <span className="text-[10px] font-black uppercase tracking-[0.14em] text-q-faint">{w.source === "ai" ? "Made with AI" : "My photo"}</span>
          <div className="flex gap-0.5">
            {!active && <button className="q-btn q-btn-ghost px-2 py-1 text-[11px]" onClick={onUse} data-testid={`my-wallpaper-use-btn-${w.id}`}>Use as wallpaper</button>}
            <button className="q-btn q-btn-ghost px-1.5 py-1 hover:text-red-300" onClick={onDelete} title="Delete" aria-label="Delete wallpaper" data-testid={`my-wallpaper-delete-btn-${w.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      }>
      {src ? <img src={src} alt={w.title} className="h-full w-full object-cover" /> : <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-q-faint" />}
    </Tile>
  );
}

export default function WallpaperTab() {
  const { look, update, replace } = useLook();
  const [gallery, setGallery] = useState([]);
  const [mine, setMine] = useState(null);
  const [dim, setDim] = useState(look.dim);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const wp = look.wallpaper;

  const load = () => api.get("/me/wallpapers").then(({ data }) => { setGallery(data.gallery); setMine(data.wallpapers); });
  useEffect(() => { load(); }, []);

  const pick = (wallpaper) => update({ wallpaper });
  const onDim = ([v]) => { setDim(v); update({ dim: v }, { debounce: true }); };

  const upload = async (file) => {
    setUploadError("");
    if (!file) return;
    if (!imageOk(file)) { setUploadError(BAD); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/wallpapers/upload", fd);
      setMine((m) => [data, ...(m || [])]);
      toast.success("Added to My wallpapers");
    } catch (e) { setUploadError(errMsg(e, BAD)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const remove = async (w) => {
    try {
      const { data } = await api.delete(`/me/wallpapers/${w.id}`);
      replace(data);
      setMine((m) => m.filter((x) => x.id !== w.id));
      toast.success("Wallpaper deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-10" data-testid="wallpaper-tab">
      <section className="q-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="q-label mb-0" htmlFor="dim-slider">Dim</label>
          <span className="font-display text-sm font-bold text-q-text" data-testid="wallpaper-dim-value">{dim}%</span>
        </div>
        <Slider id="dim-slider" className="mt-4" min={0} max={80} step={5} value={[dim]} onValueChange={onDim} data-testid="wallpaper-dim-slider" aria-label="Dim" />
        <p className="mt-3 text-xs text-q-muted">Darkens the wallpaper so text stays easy to read. The sidebar always stays solid.</p>
      </section>

      <section>
        <h2 className="q-eyebrow mb-4">Gallery</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          <Tile active={wp.type === "none"} onClick={() => pick({ type: "none", id: null })} testid="wallpaper-none-card" title="None">
            <span className="absolute inset-0 flex items-center justify-center text-q-faint"><Ban className="h-7 w-7" /></span>
          </Tile>
          {gallery.map((g) => (
            <Tile key={g.id} active={wp.type === "gallery" && wp.id === g.id} onClick={() => pick({ type: "gallery", id: g.id })} testid={`wallpaper-gallery-${g.id}`} title={g.title}>
              <img src={galleryThumb(g.id)} alt={g.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </Tile>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="q-eyebrow">My wallpapers</h2>
          <button className="q-btn q-btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="wallpaper-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload a photo
          </button>
          <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/*" onChange={(e) => upload(e.target.files?.[0])} data-testid="wallpaper-upload-input" />
        </div>
        {uploadError && <p className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-300" data-testid="wallpaper-upload-error">{uploadError}</p>}
        {mine === null ? <Loader2 className="h-5 w-5 animate-spin text-q-faint" /> : mine.length === 0 ? (
          <p className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-q-line px-5 py-8 text-center text-sm text-q-muted" data-testid="my-wallpapers-empty">
            <ImagePlus className="h-4 w-4" /> Upload a photo or make one with AI. Only you can see these.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="my-wallpapers-grid">
            {mine.map((w) => <MineTile key={w.id} w={w} active={wp.type === "mine" && wp.id === w.id} onUse={() => pick({ type: "mine", id: w.id })} onDelete={() => remove(w)} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="q-eyebrow mb-4 inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Make one with AI</h2>
        <AiWallpaper onKept={(w) => setMine((m) => [w, ...(m || []).filter((x) => x.id !== w.id)])} />
      </section>
    </div>
  );
}
